// Phase 19.18 — Live Quote Editor.
// Split-pane: form sidebar on the left, real-time PDF-style preview on the
// right. Preview sections are reorderable via up/down chips, inline editable
// via contentEditable, and the whole layout + colors + logo can be saved as
// a reusable template per tenant.
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Loader2, Save, ArrowUp, ArrowDown, Trash2, ImagePlus, Plus, Sparkles, FileDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTION_LABELS = {
  logo: "Logo",
  header: "Header (Quote # / Date)",
  client: "Client info",
  description: "Description",
  items: "Line items",
  totals: "Totals",
  notes: "Notes",
};
const DEFAULT_SECTIONS = ["logo", "header", "client", "description", "items", "totals", "notes"];
const DEFAULT_DESIGN = {
  primary_color: "#1F2A44",
  accent_color: "#0EA5E9",
  font_style: "sans-serif",
  logo_url: "",
};

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);

const QuoteEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTemplateId = searchParams.get("template");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(null);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [design, setDesign] = useState(DEFAULT_DESIGN);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const fileInputRef = useRef(null);

  // Load quote + templates in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [qRes, tplRes] = await Promise.all([
          axios.get(`${API}/quotes/${id}`),
          axios.get(`${API}/doc-templates?doc_type=quote`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setQuote({
          ...qRes.data,
          items: qRes.data.items || [],
        });
        setTemplates(tplRes.data || []);
      } catch (err) {
        toast.error("Failed to load quote");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const update = useCallback((path, value) => {
    setQuote((q) => ({ ...q, [path]: value }));
  }, []);
  const updateItem = useCallback((idx, key, value) => {
    setQuote((q) => {
      const items = [...(q.items || [])];
      items[idx] = { ...items[idx], [key]: value };
      // Auto-recompute total_price when qty/unit changes
      if (key === "quantity" || key === "unit_price") {
        const qty = key === "quantity" ? value : items[idx].quantity;
        const unit = key === "unit_price" ? value : items[idx].unit_price;
        items[idx].total_price = (Number(qty) || 0) * (Number(unit) || 0);
      }
      return { ...q, items };
    });
  }, []);
  const addItem = () => setQuote((q) => ({ ...q, items: [...(q.items || []), { description: "", quantity: 1, unit_price: 0, total_price: 0 }] }));
  const removeItem = (idx) => setQuote((q) => ({ ...q, items: (q.items || []).filter((_, i) => i !== idx) }));

  // Section move/remove
  const moveSection = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSections(next);
  };
  const removeSection = (key) => setSections((s) => s.filter((x) => x !== key));
  const addSection = (key) => setSections((s) => (s.includes(key) ? s : [...s, key]));

  // Logo upload (data-URL stays client-side; backend can host it later)
  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setDesign((d) => ({ ...d, logo_url: ev.target.result }));
    reader.readAsDataURL(f);
  };

  // Live totals
  const computedSubtotal = useMemo(
    () => (quote?.items || []).reduce((s, it) => s + (Number(it.total_price) || 0), 0),
    [quote?.items]
  );
  const taxRate = Number(quote?.tax_rate) || 0;
  const taxAmt = (computedSubtotal * taxRate) / 100;
  const total = computedSubtotal + taxAmt;

  // Phase F.4 — admin-only profitability roll-up.
  const profitability = useMemo(() => {
    const items = quote?.items || [];
    let revenue = 0;
    let cost = 0;
    for (const it of items) {
      const qty = Number(it.quantity) || 0;
      revenue += Number(it.total_price) || qty * (Number(it.unit_price) || 0);
      cost += qty * (Number(it.unit_cost) || 0);
    }
    const margin = revenue - cost;
    const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;
    return { revenue, cost, margin, marginPct };
  }, [quote?.items]);

  // Save quote (and totals)
  const onSaveQuote = async () => {
    if (!quote) return;
    setSaving(true);
    try {
      // Update top-level metadata
      const patch = {
        property_address: quote.property_address,
        description: quote.description,
        notes: quote.notes,
        tax_rate: taxRate,
        manual_customer_name: quote.manual_customer_name,
        manual_customer_email: quote.manual_customer_email,
        manual_customer_phone: quote.manual_customer_phone,
        items: (quote.items || []).map((it, idx) => ({
          description: it.description || "",
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          unit_cost: Number(it.unit_cost) || 0,
          is_client_visible: it.is_client_visible !== false,
          inventory_item_id: it.inventory_item_id || null,
          total_price: Number(it.total_price) || 0,
          sort_order: idx,
        })),
      };
      await axios.put(`${API}/quotes/${id}`, patch);
      toast.success("Quote saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  // Save current sections + design as template
  const onSaveTemplate = async () => {
    if (!templateName.trim()) { toast.error("Give the template a name first"); return; }
    try {
      const res = await axios.post(`${API}/doc-templates`, {
        name: templateName.trim(),
        doc_type: "quote",
        sections,
        design,
      });
      setTemplates((t) => [res.data, ...t]);
      setTemplateName("");
      toast.success(`Template "${res.data.name}" saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save template");
    }
  };

  const applyTemplate = (tplId) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;
    if (tpl.sections?.length) setSections(tpl.sections);
    if (tpl.design) setDesign({ ...DEFAULT_DESIGN, ...tpl.design });
    toast.success(`Applied template "${tpl.name}"`);
  };

  const onExportPdf = async () => {
    try {
      const res = await axios.post(`${API}/quotes/${id}/export`, { format: "pdf" }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `Quote-${quote.quote_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export PDF");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!quote) return <div>Quote not found.</div>;

  // ---------- Preview renderers ----------
  const renderSection = (key) => {
    switch (key) {
      case "logo":
        return (
          <div className="flex items-center justify-between">
            {design.logo_url ? (
              <img src={design.logo_url} alt="logo" style={{ maxHeight: 60, maxWidth: 180 }} />
            ) : (
              <span className="text-xs text-muted-foreground italic">(No logo — upload one in the sidebar)</span>
            )}
          </div>
        );
      case "header":
        return (
          <div className="flex justify-between items-end border-b pb-2" style={{ borderColor: design.primary_color }}>
            <h2 className="text-2xl font-bold" style={{ color: design.primary_color }}>QUOTE</h2>
            <div className="text-right text-sm">
              <div><strong>#</strong> {quote.quote_number}</div>
              <div className="text-muted-foreground">{quote.date_quoted}</div>
            </div>
          </div>
        );
      case "client":
        return (
          <div className="text-sm space-y-0.5">
            <div className="font-semibold">Bill to</div>
            <div>{quote.manual_customer_name || quote.client_name || "—"}</div>
            <div className="text-muted-foreground">{quote.manual_customer_email || ""}</div>
            <div className="text-muted-foreground">{quote.manual_customer_phone || ""}</div>
            <div className="text-muted-foreground">{quote.property_address || ""}</div>
          </div>
        );
      case "description":
        return quote.description ? (
          <p className="text-sm leading-relaxed">{quote.description}</p>
        ) : null;
      case "items":
        return (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: design.primary_color, color: "white" }}>
                <th className="text-left p-2">Description</th>
                <th className="text-right p-2 w-16">Qty</th>
                <th className="text-right p-2 w-24">Rate</th>
                <th className="text-right p-2 w-24">Total</th>
              </tr>
            </thead>
            <tbody>
              {(quote.items || []).map((it, i) => (
                <tr key={i} className={`border-b ${it.is_client_visible === false ? "opacity-60 bg-amber-50 dark:bg-amber-950/20" : ""}`}>
                  <td className="p-2">
                    {it.description || <span className="text-muted-foreground italic">(empty)</span>}
                    {it.is_client_visible === false && (
                      <span className="ml-2 inline-block text-[10px] uppercase tracking-wide bg-amber-200 text-amber-900 dark:bg-amber-700 dark:text-amber-50 px-1.5 py-0.5 rounded font-semibold align-middle">Admin only</span>
                    )}
                  </td>
                  <td className="p-2 text-right">{it.quantity}</td>
                  <td className="p-2 text-right">{fmt(it.unit_price)}</td>
                  <td className="p-2 text-right">{fmt(it.total_price)}</td>
                </tr>
              ))}
              {(quote.items || []).length === 0 && (
                <tr><td colSpan={4} className="p-3 text-center text-muted-foreground">No line items</td></tr>
              )}
            </tbody>
          </table>
        );
      case "totals":
        return (
          <div className="ml-auto w-64 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmt(computedSubtotal)}</span></div>
            {taxRate > 0 && <div className="flex justify-between"><span>Tax ({taxRate}%)</span><span>{fmt(taxAmt)}</span></div>}
            <div className="flex justify-between text-base font-bold border-t pt-1" style={{ color: design.primary_color }}>
              <span>Total</span><span>{fmt(total)}</span>
            </div>
          </div>
        );
      case "notes":
        return quote.notes ? (
          <div className="text-xs text-muted-foreground border-t pt-2"><strong>Notes:</strong> {quote.notes}</div>
        ) : null;
      default:
        return null;
    }
  };

  const fontFamily =
    design.font_style === "serif" ? "Georgia, serif" :
    design.font_style === "mono" ? "ui-monospace, monospace" : "Inter, system-ui, sans-serif";

  const missingSections = Object.keys(SECTION_LABELS).filter((k) => !sections.includes(k));

  return (
    <div className="grid lg:grid-cols-[420px_minmax(0,1fr)] gap-4 h-[calc(100vh-7rem)]" data-testid="quote-editor-page">
      {/* Sidebar */}
      <div className="overflow-y-auto pr-2 space-y-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Templates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Select onValueChange={applyTemplate}>
              <SelectTrigger data-testid="apply-template-select">
                <SelectValue placeholder={templates.length ? "Apply a template…" : "(no templates yet)"} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input
                placeholder="Save current layout as…"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                data-testid="template-name-input"
              />
              <Button size="sm" onClick={onSaveTemplate} data-testid="save-template-btn">Save</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Document fields</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <Label className="text-xs">Customer name</Label>
              <Input value={quote.manual_customer_name || quote.client_name || ""} onChange={(e) => update("manual_customer_name", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={quote.manual_customer_email || ""} onChange={(e) => update("manual_customer_email", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={quote.manual_customer_phone || ""} onChange={(e) => update("manual_customer_phone", e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Property / address</Label>
              <Input value={quote.property_address || ""} onChange={(e) => update("property_address", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={quote.description || ""} onChange={(e) => update("description", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={quote.notes || ""} onChange={(e) => update("notes", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Tax rate (%)</Label>
              <Input type="number" step="0.01" value={quote.tax_rate ?? 0} onChange={(e) => update("tax_rate", parseFloat(e.target.value) || 0)} />
            </div>

            <div className="space-y-2 pt-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs">Line items</Label>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="editor-add-item"><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              {(quote.items || []).map((it, idx) => (
                <div key={idx} className="border rounded p-2 space-y-1 bg-muted/30" data-testid={`editor-line-item-${idx}`}>
                  <Input placeholder="Description" value={it.description || ""} onChange={(e) => updateItem(idx, "description", e.target.value)} />
                  <div className="grid grid-cols-3 gap-1">
                    <Input placeholder="Qty" type="number" step="any" value={it.quantity ?? 1} onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} />
                    <Input placeholder="Unit" type="number" step="0.01" value={it.unit_price ?? 0} onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} />
                    <Input placeholder="Total" type="number" step="0.01" value={it.total_price ?? 0} onChange={(e) => updateItem(idx, "total_price", parseFloat(e.target.value) || 0)} />
                  </div>
                  {/* Phase F.4 — admin cost + per-line client visibility */}
                  <div className="grid grid-cols-2 gap-1 pt-1">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">My cost (admin)</Label>
                      <Input
                        placeholder="0.00" type="number" step="0.01"
                        value={it.unit_cost ?? 0}
                        onChange={(e) => updateItem(idx, "unit_cost", parseFloat(e.target.value) || 0)}
                        data-testid={`editor-line-item-${idx}-cost`}
                      />
                    </div>
                    <div className="flex items-end justify-end pb-1">
                      <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={it.is_client_visible !== false}
                          onChange={(e) => updateItem(idx, "is_client_visible", e.target.checked)}
                          data-testid={`editor-line-item-${idx}-visible`}
                          className="h-4 w-4 rounded border-input"
                        />
                        <span className={it.is_client_visible === false ? "text-amber-600 font-medium" : ""}>
                          {it.is_client_visible === false ? "Admin only" : "Visible to client"}
                        </span>
                      </label>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-destructive h-7" data-testid={`editor-remove-item-${idx}`}><Trash2 className="h-3 w-3 mr-1" /> Remove</Button>
                </div>
              ))}
            </div>

            {/* Phase F.4 — Profitability roll-up (admin-only, never exposed to client) */}
            <div className="mt-3 p-2 rounded border border-dashed bg-amber-50/50 dark:bg-amber-950/20 space-y-1" data-testid="quote-profitability-card">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wide font-bold text-amber-700 dark:text-amber-300">
                  Quote Assistant · Admin only
                </span>
                <span className={`text-xs font-bold ${profitability.margin >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`} data-testid="profitability-margin-pct">
                  {profitability.marginPct.toFixed(1)}% margin
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1 text-xs">
                <div>
                  <div className="text-muted-foreground">Revenue</div>
                  <div className="font-semibold">{fmt(profitability.revenue)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">My cost</div>
                  <div className="font-semibold">{fmt(profitability.cost)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Profit</div>
                  <div className={`font-semibold ${profitability.margin >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>{fmt(profitability.margin)}</div>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground italic">
                Margin = sum of (qty × unit price) − (qty × my cost) across all lines.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Design</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Primary color</Label>
                <Input type="color" value={design.primary_color} onChange={(e) => setDesign((d) => ({ ...d, primary_color: e.target.value }))} className="h-9 p-1" />
              </div>
              <div>
                <Label className="text-xs">Accent color</Label>
                <Input type="color" value={design.accent_color} onChange={(e) => setDesign((d) => ({ ...d, accent_color: e.target.value }))} className="h-9 p-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Font</Label>
              <Select value={design.font_style} onValueChange={(v) => setDesign((d) => ({ ...d, font_style: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sans-serif">Sans-serif</SelectItem>
                  <SelectItem value="serif">Serif</SelectItem>
                  <SelectItem value="mono">Monospace</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Logo</Label>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onPickLogo} />
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-logo-btn">
                <ImagePlus className="h-3 w-3 mr-1" /> {design.logo_url ? "Replace logo" : "Upload logo"}
              </Button>
              {design.logo_url && (
                <Button size="sm" variant="ghost" onClick={() => setDesign((d) => ({ ...d, logo_url: "" }))} className="ml-2">Clear</Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Section order</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            {sections.map((k, idx) => (
              <div key={k} className="flex items-center gap-1 bg-muted/30 rounded p-1.5" data-testid={`section-row-${k}`}>
                <span className="flex-1 text-xs">{SECTION_LABELS[k] || k}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => moveSection(idx, -1)} disabled={idx === 0} data-testid={`section-up-${k}`}><ArrowUp className="h-3 w-3" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => moveSection(idx, +1)} disabled={idx === sections.length - 1} data-testid={`section-down-${k}`}><ArrowDown className="h-3 w-3" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeSection(k)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
            {missingSections.length > 0 && (
              <div className="pt-2 border-t">
                <div className="text-xs text-muted-foreground mb-1">Add a section:</div>
                <div className="flex flex-wrap gap-1">
                  {missingSections.map((k) => (
                    <Button key={k} size="sm" variant="outline" className="h-6 text-xs" onClick={() => addSection(k)}>
                      <Plus className="h-3 w-3 mr-1" />{SECTION_LABELS[k]}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="sticky bottom-0 bg-background/95 backdrop-blur pt-2 pb-1 flex gap-2">
          <Button onClick={onSaveQuote} disabled={saving} className="flex-1" data-testid="save-quote-editor-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save quote
          </Button>
          <Button variant="outline" onClick={onExportPdf} data-testid="export-pdf-editor-btn">
            <FileDown className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/quotes/${id}`)}>Back</Button>
        </div>
      </div>

      {/* Preview pane */}
      <div className="overflow-y-auto">
        <div
          className="bg-white shadow-lg mx-auto p-10 space-y-4"
          style={{
            fontFamily,
            maxWidth: 820,
            minHeight: 1056,
            color: "#1a1a1a",
          }}
          data-testid="quote-preview"
        >
          {sections.map((k) => (
            <div key={k} className="space-y-2">
              {renderSection(k)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuoteEditor;
